/**
 * ValidarFuec Component
 * Vista pública para validar un FUEC escaneando el código QR
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Result,
  Spin,
  Descriptions,
  Tag,
  Typography,
  Space,
  Alert,
  Button,
  Divider,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  QrcodeOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CarOutlined,
  BankOutlined,
  PrinterOutlined,
  ShareAltOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  CalendarOutlined,
  HomeOutlined
} from '@ant-design/icons';
import axios from 'axios';
import './ValidarControlServicio.css';
import logoMogotax from '../../Assets/img/logo.png';

const { Title, Text, Paragraph } = Typography;

const ValidarFuec = () => {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [valido, setValido] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    validarFuec();
    // eslint-disable-next-line
  }, [codigo]);

  const validarFuec = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/fuec/validar-publico/${codigo}`
      );

      if (response.data.success) {
        setData(response.data);
        setValido(response.data.valido);
      } else {
        setValido(false);
        setError(response.data.message || 'FUEC no valido');
      }
    } catch (err) {
      setValido(false);
      setError(
        err.response?.data?.message ||
        'Error al validar el FUEC. Por favor, verifique el codigo e intente nuevamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const getEstadoVigenciaConfig = (estado) => {
    const config = {
      vigente: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        text: 'VIGENTE',
        resultStatus: 'success',
        resultIcon: <SafetyCertificateOutlined style={{ color: '#52c41a' }} />
      },
      vencido: {
        color: 'error',
        icon: <CloseCircleOutlined />,
        text: 'VENCIDO',
        resultStatus: 'error',
        resultIcon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      },
      anulado: {
        color: 'error',
        icon: <CloseCircleOutlined />,
        text: 'ANULADO',
        resultStatus: 'error',
        resultIcon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      },
      pendiente: {
        color: 'warning',
        icon: <ClockCircleOutlined />,
        text: 'PENDIENTE',
        resultStatus: 'warning',
        resultIcon: <WarningOutlined style={{ color: '#faad14' }} />
      }
    };

    return config[estado] || config.vencido;
  };

  const handleImprimir = () => {
    window.print();
  };

  const handleCompartir = async () => {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `FUEC - ${data?.fuec?.numero_fuec}`,
          text: `Validacion de FUEC MOGOTAX - ${data?.contratante?.nombre}`,
          url: url,
        });
      } catch (err) {
        console.log('Error al compartir:', err);
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('Enlace copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="validar-control-container">
        <div className="validar-control-content-modern">
          <Card className="validar-control-card-modern">
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
              <Title level={4} style={{ marginTop: 20 }}>
                Validando FUEC...
              </Title>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="validar-control-container">
        <div className="validar-control-content-modern">
          <Card className="validar-control-card-modern">
            <Result
              status="error"
              title="FUEC No Encontrado"
              subTitle={error}
              extra={[
                <Button type="primary" key="home" onClick={() => navigate('/')}>
                  Volver al Inicio
                </Button>,
              ]}
            />
          </Card>
        </div>
      </div>
    );
  }

  const estadoConfig = getEstadoVigenciaConfig(data?.estado_vigencia);

  return (
    <div className="validar-control-container">
      <div className="validar-control-content-modern">
        <Card className="validar-control-card-modern">
          {/* Header */}
          <div className="control-header-modern">
            <div className="header-logo-modern">
              <img
                src={logoMogotax}
                alt="MOGOTAX SAS - Validacion de FUEC"
                className="company-logo"
              />
            </div>
            <div className="header-actions">
              <Space>
                <Tooltip title="Imprimir">
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handleImprimir}
                    className="no-print"
                  />
                </Tooltip>
                <Tooltip title="Compartir">
                  <Button
                    icon={<ShareAltOutlined />}
                    onClick={handleCompartir}
                    className="no-print"
                  />
                </Tooltip>
              </Space>
            </div>
          </div>

          <Divider />

          {/* Estado de Validacion */}
          <Result
            status={estadoConfig.resultStatus}
            icon={estadoConfig.resultIcon}
            title={valido ? "FUEC Verificado y Vigente" : `FUEC ${estadoConfig.text}`}
            subTitle={
              <Text>
                {data?.mensaje_vigencia}
                {data?.dias_restantes !== null && valido && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {data.dias_restantes} dias restantes
                  </Tag>
                )}
              </Text>
            }
            style={{ padding: '20px 0' }}
          />

          {/* Numero FUEC destacado */}
          <Card
            style={{
              marginBottom: 24,
              background: valido
                ? 'linear-gradient(135deg, #1a5276 0%, #2980b9 100%)'
                : 'linear-gradient(135deg, #7f8c8d 0%, #95a5a6 100%)',
              textAlign: 'center',
            }}
          >
            <Title level={3} style={{ color: '#fff', margin: 0 }}>
              FUEC No. {data?.fuec?.numero_fuec}
            </Title>
            <Text style={{ color: '#d6eaf8' }}>
              Consecutivo: {data?.fuec?.consecutivo} | Version: {data?.fuec?.version_contrato}
            </Text>
          </Card>

          {/* Layout de 2 Columnas */}
          <Row gutter={[24, 24]}>
            {/* Columna Izquierda */}
            <Col xs={24} lg={12}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Empresa Transportadora */}
                <Card
                  type="inner"
                  title={<><BankOutlined /> Empresa Transportadora</>}
                  className="info-card"
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Razon Social">
                      <Text strong>{data?.empresa?.nombre}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="NIT">
                      {data?.empresa?.nit}
                    </Descriptions.Item>
                    <Descriptions.Item label="Codigo Habilitacion">
                      <Tag color="blue">{data?.empresa?.codigo_habilitacion}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Direccion">
                      {data?.empresa?.direccion}
                    </Descriptions.Item>
                    <Descriptions.Item label="Telefono">
                      {data?.empresa?.telefono}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Contratante */}
                <Card
                  type="inner"
                  title={<><HomeOutlined /> Contratante</>}
                  className="info-card"
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nombre">
                      <Text strong>{data?.contratante?.nombre}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="NIT/CC">
                      {data?.contratante?.nit || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="No. Contrato">
                      <Tag color="purple">{data?.fuec?.numero_contrato}</Tag>
                    </Descriptions.Item>
                    {data?.contratante?.convenio_nombre && (
                      <>
                        <Descriptions.Item label="Convenio">
                          {data?.contratante?.convenio_nombre}
                        </Descriptions.Item>
                        <Descriptions.Item label="NIT Convenio">
                          {data?.contratante?.convenio_nit}
                        </Descriptions.Item>
                      </>
                    )}
                    {data?.contratante?.responsable && (
                      <>
                        <Descriptions.Item label="Responsable">
                          {data.contratante.responsable.nombre}
                        </Descriptions.Item>
                        <Descriptions.Item label="Identificación Responsable">
                          {data.contratante.responsable.identificacion || 'N/A'}
                        </Descriptions.Item>
                      </>
                    )}
                  </Descriptions>
                </Card>

                {/* Vigencia */}
                <Card
                  type="inner"
                  title={<><CalendarOutlined /> Vigencia del Contrato</>}
                  className="info-card"
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Fecha Inicio">
                      <Text strong>{data?.vigencia?.fecha_inicio}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Fecha Fin">
                      <Text strong>{data?.vigencia?.fecha_fin}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Estado">
                      <Tag icon={estadoConfig.icon} color={estadoConfig.color}>
                        {estadoConfig.text}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Space>
            </Col>

            {/* Columna Derecha */}
            <Col xs={24} lg={12}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Vehiculo */}
                <Card
                  type="inner"
                  title={<><CarOutlined /> Caracteristicas del Vehiculo</>}
                  className="info-card"
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Placa">
                      <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {data?.vehiculo?.placa}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Marca">
                      {data?.vehiculo?.marca || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Modelo">
                      {data?.vehiculo?.modelo || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Clase">
                      {data?.vehiculo?.clase || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="No. Interno">
                      {data?.vehiculo?.numero_interno || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tarjeta Operacion">
                      {data?.vehiculo?.tarjeta_operacion || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Conductor */}
                <Card
                  type="inner"
                  title={<><UserOutlined /> Datos del Conductor</>}
                  className="info-card"
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nombre">
                      <Text strong>{data?.conductor?.nombre}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Cedula">
                      {data?.conductor?.cedula}
                    </Descriptions.Item>
                    <Descriptions.Item label="Licencia">
                      {data?.conductor?.licencia || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Vigencia Licencia">
                      {data?.conductor?.vigencia_licencia || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Origen y Destino */}
                <Card
                  type="inner"
                  title={<><EnvironmentOutlined /> Origen y Destino</>}
                  className="info-card"
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <EnvironmentOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      <Text strong>Origen:</Text>
                      <Text style={{ marginLeft: 8 }}>{data?.ruta?.origen}</Text>
                    </div>
                    <div>
                      <EnvironmentOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                      <Text strong>Destino:</Text>
                      <Text style={{ marginLeft: 8 }}>{data?.ruta?.destino}</Text>
                    </div>
                    {data?.ruta?.tipo_recorrido && (
                      <div>
                        <Tag color="orange">{data?.ruta?.tipo_recorrido}</Tag>
                      </div>
                    )}
                  </Space>
                </Card>
              </Space>
            </Col>
          </Row>

          {/* Objeto del Contrato */}
          {data?.objeto_contrato && (
            <>
              <Divider />
              <Card
                type="inner"
                title={<><FileTextOutlined /> Objeto del Contrato</>}
                className="info-card"
              >
                <Paragraph style={{ textAlign: 'justify', margin: 0 }}>
                  {data?.objeto_contrato}
                </Paragraph>
              </Card>
            </>
          )}

          <Divider />

          {/* Footer */}
          <Alert
            message={valido ? "Documento Oficial Verificado" : "Documento No Valido"}
            description={
              <div>
                <Paragraph style={{ margin: 0 }}>
                  {valido ? (
                    <>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      Este FUEC ha sido verificado digitalmente por el sistema MOGOTAX.
                      La informacion aqui contenida es oficial y autentica.
                    </>
                  ) : (
                    <>
                      <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                      {data?.mensaje_vigencia}. Por favor verifique con la empresa transportadora.
                    </>
                  )}
                </Paragraph>
                <Paragraph style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                  Fecha de verificacion: {new Date().toLocaleString('es-CO')}
                </Paragraph>
                <Paragraph style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                  Generado el: {data?.fuec?.fecha_generacion}
                </Paragraph>
              </div>
            }
            type={valido ? "success" : "warning"}
            showIcon
            icon={valido ? <SafetyCertificateOutlined /> : <WarningOutlined />}
          />
        </Card>
      </div>
    </div>
  );
};

export default ValidarFuec;
